import React, { useState, useEffect, useRef } from "react";
import { Loader2 } from "lucide-react";
import "./style.css";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app, db } from "../Admin/Config/Config";
import CallErrorPage from "./CallErrorPage";
import {
  LocalUser,
  RemoteUser,
  useIsConnected,
  useJoin,
  useLocalMicrophoneTrack,
  usePublish,
  useRemoteUsers,
  useRTCClient,
} from "agora-rtc-react";
import CallconnectedPage from "./CallConnectedPage";
import { doc, onSnapshot, updateDoc } from "firebase/firestore";
export default function Callui({ status,topic, onError, ...callDetails }) {
  // ========== PAGE OPENING ============ //
  const [isOpen, setIsOpen] = useState(false);
  const [callStatus, setCallStatus] = useState("idle");

  useEffect(() => {
    setIsOpen(status);
  }, [status, callDetails]);

  // =================== CALL DETAILS ====================== //
  const [caller, setCaller] = useState({});
  const [receiver, setReceiver] = useState({});
  const [inCall, setInCall] = useState(false);
  const [callConnection, setCallConnection] = useState(null);

  // =============== CALLING TOKEN GENERATION FUNCTION ============== //
  useEffect(() => {
    if (!hasCalledRef.current && callStatus === "idle") {
      handleCall(callDetails);
      hasCalledRef.current = true;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callStatus, callDetails]);

  // ================ FUNCTION TO GENERATE TOKEN ================ //
  const [callError, setCallError] = useState(null);
  const handleCall = async (call) => {
    setCaller(call.callDetails.sender);
    setReceiver(call.callDetails.reciever);
    setCallStatus("connecting");

    const functions = getFunctions(app, "asia-south1");
    const createToken = httpsCallable(functions, "createTokenAgoraCaller");
    try {
      const result = await createToken({
        sender_uid: call.callDetails.sender.uid,
        receiver_uid: call.callDetails.reciever.uid,
        topic: topic
      });
      if (result.data.code === 200) {
        const data = result.data;
        setCallConnection({
          app_id: data.app_id.trim(),
          token: data.token.trim(),
          channel_name: data.channel_name,
          uid: data.uid,
        });
        startListening(data.channel_name);
        setInCall(true);
      } else {
        throw new Error(result.data.message);
      }
    } catch (err) {
      setCallStatus("error");
      setCallError(err.message);
      console.error("Error calling function:", err.message);
    }
  };

  const retryCall = () => {
    handleCall(callDetails);
  };

  // ===============================================================================================================  //
  //                             <------- AGORA CONFIGURATIONS AND CALL JOINING ------->                           //
  // ===============================================================================================================  //

  const [isMuted, setIsMuted] = useState(false);
  const isConnected = useIsConnected();
  const hasCalledRef = useRef(false);
  const client = useRTCClient();
  // ======================= CALL JOINING ====================== //
  useJoin(
    callConnection &&
      callConnection.token &&
      callConnection.app_id &&
      callConnection.channel_name
      ? {
          appid: callConnection.app_id,
          channel: callConnection.channel_name,
          token: callConnection.token,
        }
      : null,
    inCall
  );
 // ====================== LOCAL AUDIO TRACK ====================== //
  const {
    localMicrophoneTrack,
    isLoading: isTrackLoading,
    error: trackError,
  } = useLocalMicrophoneTrack({
    ANS: true, 
    AEC: true, 
  });
 // ====================== PUBLISHING LOCAL AUDIO TRACK ====================== //
  usePublish(localMicrophoneTrack ? [localMicrophoneTrack] : []);
  useEffect(() => {

    if (localMicrophoneTrack) {
      // realTimeCallFetch();
      localMicrophoneTrack.setEnabled(!isMuted);
    }
  }, [isMuted, localMicrophoneTrack]);

  const remoteUsers = useRemoteUsers();
// ====================== SUBSCRIBING TO REMOTE AUDIO TRACK ====================== //
  useEffect(() => {
    const handleUserPublished = async (user, mediaType) => {
      if (mediaType === "audio") {
        await client.subscribe(user, mediaType);
        // console.log(`Subscribed to audio track of user ${user.uid}`);
        user.audioTrack?.play();
      }
    };
    // ====================== UNSUBSCRIBING TO REMOTE AUDIO TRACK ====================== //
    const handleUserUnpublished = (user, mediaType) => {
      if (mediaType === "audio") {
        // console.log(`Unsubscribed from audio track of user ${user.uid}`);
      }
    };

    client.on("user-published", handleUserPublished);
    client.on("user-unpublished", handleUserUnpublished);

    return () => {
      client.off("user-published", handleUserPublished);
      client.off("user-unpublished", handleUserUnpublished);
    };
  }, [client]);
  // ====================== CLOSING LOCAL AUDIO TRACK ====================== //
  useEffect(() => {
    return () => {
      if (localMicrophoneTrack) {
        localMicrophoneTrack.close();
      }
    };
  }, [localMicrophoneTrack]);
  // ====================== ENDING CALL ====================== //
  const [isLoading, setIsLoading] = useState(false);
 const [reciever, setReciever] = useState(null);
  useEffect(()=>{
    if(remoteUsers.length>0){
      setCallStatus("connected");
      setReciever(remoteUsers);
    }
    if(remoteUsers.length===0 && reciever){
      stopListening(callConnection.channel_name);
      setReciever(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[remoteUsers]);

  if (trackError) {
    // console.error("Failed to access microphone:", trackError);
    return <div>Please grant microphone permissions to start the call.</div>;
  }
  // ===================================== CALL CONNECTION CHECK ====================================  //
  
let unsubscribe = null;
const startListening = (name) => {
  const docRef = doc(db, "bristu_talk_call_invites", name);
  unsubscribe = onSnapshot(
    docRef,
    (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        if (data.status === "disconnected" || data.status === "missed") {
          stopListening(name);
        }
      }
    },
    (error) => {
      console.error("Listener error:", error);
      stopListening(name);
    }
  );
};
const stopListening = async (name) => {
  if (unsubscribe) {
    unsubscribe(); 
    unsubscribe = null;
      setIsLoading(true);
    const functions = getFunctions(app, "asia-south1");
    const endCalling = httpsCallable(functions, "endCall");
    try {
      await endCalling({ channel_name: name });
        setInCall(false);
        setCallConnection(null);
        setCallStatus("error");
        setCallError("Call disconnected");
        setIsLoading(false);
    } catch (err) {
      console.error("Error calling function:", err.message);
      setIsLoading(false);
    }
  } else {
    await updateDoc(doc(db, "bristu_talk_call_invites", name), {
      status: "disconnected",
    })
  }
};
  return (
    <>
      {isOpen && (
        <div className="flex items-center justify-center min-h-screen">
          <div className="px-8 py-8 bg-black rounded-2xl flex flex-col items-center justify-center max-w-4xl w-full">
            {callStatus !== "idle" && (
              <div className="flex flex-col space-y-6 w-full animate-fade-in">
                {callStatus === "connecting" ? (
                  <div className="w-full text-center space-y-6 animate-fade-in">
                    <div className="flex items-center justify-center gap-8">
                      <div className="relative">
                        <div className="w-24 h-24 bg-blue-100 rounded-full flex items-center justify-center text-2xl font-bold text-blue-600 shadow-lg">
                          {caller &&
                            caller.name
                              .split(" ")
                              .map((word) => word.charAt(0).toUpperCase())
                              .join("")}
                        </div>
                        <div className="absolute inset-0 border-4 border-blue-200 rounded-full animate-ping-slow" />
                      </div>
                      <Loader2 className="w-12 h-12 text-blue-500 animate-spin" />
                      <div className="relative">
                        <div className="w-24 h-24 bg-purple-100 rounded-full flex items-center justify-center text-2xl font-bold text-purple-600 shadow-lg">
                          {receiver &&
                            receiver.name
                              .split(" ")
                              .map((word) => word.charAt(0).toUpperCase())
                              .join("")}
                        </div>
                        <div className="absolute inset-0 border-4 border-purple-200 rounded-full animate-ping-slow" />
                      </div>
                    </div>
                    <div className="flex flex-col items-center space-y-6 p-2 bg-black ">
                      <div className="relative flex flex-col items-center">
                        <h2 className="mt-4 text-2xl font-semibold text-gray-100 animate-pulse">
                          Connecting Call...
                        </h2>
                        <p className="text-gray-400 text-sm mt-2">
                          Please wait while we connect your call
                        </p>
                      </div>

                      <div className="w-full flex justify-center space-x-4">
                        <button
                          onClick={() =>
                            stopListening(callConnection.channel_name)
                          }
                          className="p-3 bg-red-500 hover:bg-red-600 rounded-full transition-all duration-200  shadow-red-100"
                        >
                          <svg
                            className="w-6 h-6 text-white"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                ) : callStatus === "connected" ? (
                  <>
                    {" "}
                    {topic && (
                      <p className="text-gray-100 text-sm mt-2">
                        Your topic is {topic}
                      </p>
                    )}
                    <CallconnectedPage
                      isConnected={isConnected}
                      onMute={() => setIsMuted(!isMuted)}
                      channelName={callConnection?.channel_name ?? ""}
                      isLoading={isLoading}
                      endCall={() =>
                        stopListening(callConnection?.channel_name ?? "")
                      }
                    />
                  </>
                ) : null}
                {callStatus === "error" && (
                  <CallErrorPage
                    error={callError}
                    onRetry={retryCall}
                    onClose={onError}
                  />
                )}
                {isConnected && (
                  <div className="flex flex-col items-center space-y-6">
                    {isTrackLoading ? (
                      <div>Loading audio track...</div>
                    ) : (
                      <>
                        {localMicrophoneTrack && (
                          <LocalUser
                            audioTrack={localMicrophoneTrack}
                            micOn={!isMuted}
                            cover="https://www.agora.io/en/wp-content/uploads/2022/10/3d-spatial-audio-icon.svg"
                            playAudio={false}
                          >
                            <samp className="user-name">You</samp>
                          </LocalUser>
                        )}
                        {remoteUsers.map((user) => (
                          <div className="user" key={user.uid}>
                            <RemoteUser
                              cover="https://www.agora.io/en/wp-content/uploads/2022/10/3d-spatial-audio-icon.svg"
                              user={user}
                            >
                              <samp className="user-name">{user.uid}</samp>
                            </RemoteUser>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
